/**
 * Güvenlik Utility Fonksiyonları
 * XSS ve diğer güvenlik açıklarını önlemek için kullanılır
 */

/**
 * HTML karakterlerini escape eder (XSS koruması)
 * @param {string} str - Escape edilecek string
 * @returns {string} - Escape edilmiş string
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') {
    return String(str || '');
  }

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  return str.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * String'i güvenli bir şekilde sanitize eder (React JSX {…} metin gösterimi için).
 * HTML etiketleri kaldırılır; `/` ve benzeri karakterler entity'ye çevrilmez — aksi halde
 * API'den gelen "ÜKY / D2" gibi metinler ekranda `&#x2F;` olarak görünür.
 * Özel karakterler JSX metin düğümünde React tarafından zaten güvenli işlenir.
 *
 * @param {string} input - Sanitize edilecek input
 * @returns {string} - Sanitize edilmiş string
 */
export const sanitizeString = (input) => {
  if (input === null || input === undefined) {
    return '';
  }

  const str = String(input);

  const withoutHtml = str.replace(/<[^>]*>/g, '');

  return withoutHtml.trim();
};

/**
 * Email formatını validate eder
 * @param {string} email - Validate edilecek email
 * @returns {boolean} - Geçerli email ise true
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Arama kutusu için güvenli metin: HTML etiketleri ve denetim karakterleri kaldırılır;
 * `/` `(` `)` `:` `&` vb. ürün adı/kodunda sık karakterler korunur (NoSQL tarafında regex kaçışı backend’de yapılır).
 *
 * @param {string} searchTerm
 * @param {{ trimEdges?: boolean }} [options] - trimEdges: false → yazım sırasında boşluk korunur (API’ye göndermeden önce trim yapın)
 * @returns {string}
 */
export const sanitizeSearchInput = (searchTerm, options = {}) => {
  const trimEdges = options.trimEdges !== false;

  if (!searchTerm || typeof searchTerm !== 'string') {
    return '';
  }

  const maxLength = 100;
  let normalized = searchTerm.normalize('NFC');
  if (trimEdges) {
    normalized = normalized.trim();
  }

  if (normalized.length > maxLength) {
    normalized = normalized.substring(0, maxLength);
  }

  // XSS: yalnızca HTML tag’leri (içerik korunur, tag metni döner)
  let out = normalized.replace(/<[^>]*>/g, '');
  // C0/C1 denetim karakterleri ve bilinen sorunlu kod noktaları
  out = out.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\uFFFE\uFFFF]/g, '');

  if (trimEdges) {
    out = out.trim();
  }

  return out;
};

/**
 * Kullanıcı adını validate ve sanitize eder
 * @param {string} name - Validate edilecek isim
 * @returns {string} - Sanitize edilmiş isim
 */
export const sanitizeUserName = (name) => {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const maxLength = 100;
  const trimmed = name.trim();
  const normalized = trimmed.normalize('NFC');
  
  if (normalized.length > maxLength) {
    return normalized.substring(0, maxLength);
  }

  // Unicode harfler, rakamlar, boşluk ve bazı özel karakterlere izin ver
  const sanitized = normalized.replace(/[^\p{L}\p{N}\s.-]/gu, '');
  
  return sanitized;
};

/**
 * Object içindeki tüm string değerleri sanitize eder
 * @param {Object} obj - Sanitize edilecek object
 * @returns {Object} - Sanitize edilmiş object
 */
export const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const sanitized = {};
  
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      
      if (typeof value === 'string') {
        sanitized[key] = sanitizeString(value);
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        sanitized[key] = sanitizeObject(value);
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'string' ? sanitizeString(item) : item
        );
      } else {
        sanitized[key] = value;
      }
    }
  }
  
  return sanitized;
};

/**
 * HTML entity'lerini decode eder (gösterim için)
 * @param {string} str - Decode edilecek string
 * @returns {string} - Decode edilmiş string
 */
export const decodeHtml = (str) => {
  if (typeof str !== 'string') {
    return String(str || '');
  }

  // HTML entity map'i (tersi)
  const entityMap = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#x27;': "'",
    '&#x2F;': '/',
    '&#x60;': '`',
    '&#x3D;': '=',
  };

  // Tüm HTML entity'lerini decode et
  return str.replace(/&[#\w]+;/g, (entity) => {
    // Numeric entity'ler (&#x2F; gibi)
    if (entity.startsWith('&#x')) {
      const hex = entity.slice(3, -1);
      try {
        return String.fromCharCode(parseInt(hex, 16));
      } catch {
        return entity;
      }
    }
    // Decimal entity'ler (&#47; gibi)
    if (entity.startsWith('&#')) {
      const dec = entity.slice(2, -1);
      try {
        return String.fromCharCode(parseInt(dec, 10));
      } catch {
        return entity;
      }
    }
    // Named entity'ler (&amp; gibi)
    return entityMap[entity] || entity;
  });
};

/** MongoDB ObjectId regex (24 hex karakter) */
const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

/**
 * Buffer/array'dan ObjectId hex string'e çevirir (12 byte -> 24 hex char)
 */
const bufferToHex = (buf) => {
  if (!buf || typeof buf !== 'object') return null;
  const bytes = [];
  for (let i = 0; i < 12; i++) {
    const b = buf[i] ?? buf[String(i)];
    if (typeof b !== 'number' || b < 0 || b > 255) return null;
    bytes.push(b);
  }
  if (bytes.length !== 12) return null;
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * ID'yi geçerli MongoDB ObjectId string formatına çevirir.
 * API'ye gönderilecek icerikId, randevuId vb. için kullanılır.
 * @param {*} id - id, _id, ObjectId objesi (buffer dahil), string
 * @returns {string|null} - 24 karakter hex string veya null (geçersizse)
 */
export const toValidObjectIdString = (id) => {
  if (id === null || id === undefined) return null;
  let str;
  if (typeof id === 'object') {
    if (id.$oid) str = String(id.$oid);
    else if (id.buffer && typeof id.buffer === 'object') str = bufferToHex(id.buffer);
    else if (Array.isArray(id) && id.length >= 12) str = bufferToHex(id);
    else if (typeof id.toString === 'function') {
      const s = id.toString();
      str = OBJECT_ID_REGEX.test(s) ? s : null;
    }
    if (!str) str = String(id);
  } else {
    str = String(id).trim();
  }
  if (!str || str.length !== 24) return null;
  if (!OBJECT_ID_REGEX.test(str)) return null;
  return str.toLowerCase();
};

/**
 * API'den gelen tarihi parse eder (MongoDB Date, $date, ObjectId, ISO string, timestamp)
 * @param {*} dateValue - API'den gelen tarih değeri
 * @returns {Date|null} - Geçerli Date veya null
 */
export const parseApiDate = (dateValue) => {
  if (dateValue === null || dateValue === undefined) return null;
  if (typeof dateValue === 'number') {
    const d = new Date(dateValue);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof dateValue === 'string') {
    const d = new Date(dateValue);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof dateValue === 'object') {
    if (Object.keys(dateValue).length === 0) return null; // {}
    if (dateValue.$date) {
      const v = dateValue.$date;
      if (typeof v === 'number') return new Date(v);
      if (typeof v === 'string') {
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
      }
    }
    const hex = toValidObjectIdString(dateValue);
    if (hex) {
      const timestamp = parseInt(hex.substring(0, 8), 16) * 1000;
      return new Date(timestamp);
    }
  }
  return null;
};

/**
 * API'den gelen tarihi tr-TR formatında string'e çevirir
 * @param {*} dateValue - API'den gelen tarih değeri
 * @param {Object} options - toLocaleString seçenekleri
 * @returns {string} - Formatlanmış tarih veya '—'
 */
export const formatApiDate = (dateValue, options = {}) => {
  const defaultOpts = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  const opts = { ...defaultOpts, ...options };
  const d = parseApiDate(dateValue);
  if (!d || isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleString('tr-TR', opts);
  } catch {
    return '—';
  }
};

/**
 * URL'i validate eder
 * @param {string} url - Validate edilecek URL
 * @returns {boolean} - Geçerli URL ise true
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

